import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { EmptyState } from '@components/empty-state';
import { IconSend } from '@components/icons/send';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useTravellerActionIntegrationResponses } from 'api/traveller-actions/hooks';
import { TravellerActionIntegrationResponse } from 'api/traveller-actions/requests';

import { useRequestDetailModal } from './request-detail-modal';
import { getStatusCodeBadgeVariant } from './status-code-variant';

type RequestsTabProps = {
  actionId: string;
};

export const RequestsTab = ({ actionId }: RequestsTabProps) => {
  const { t } = useScopedTranslate(locale);
  const { data: responses, isLoading } = useTravellerActionIntegrationResponses(actionId);

  if (isLoading) {
    return <Spinner />;
  }

  if (!responses || responses.length === 0) {
    return (
      <EmptyState
        variant="small"
        icon={<IconSend />}
        header={t('emptyHeader')}
        content={t('emptyContent')}
        skipMargin
      />
    );
  }

  return (
    <Stack space={3}>
      {responses.map((response) => (
        <RequestCard key={response.shortId} actionId={actionId} response={response} />
      ))}
    </Stack>
  );
};

const RequestCard = ({
  actionId,
  response,
}: {
  actionId: string;
  response: TravellerActionIntegrationResponse;
}) => {
  const { t } = useScopedTranslate(locale);
  const showDetailModal = useRequestDetailModal();
  const requestTypeLabel = (response.requestType ?? '').toUpperCase() || t('unknownType');

  return (
    <Card
      space={4}
      interactive
      onClick={() =>
        showDetailModal({ actionId, responseShortId: response.shortId, initialResponse: response })
      }
    >
      <Stack space={3} orientation="horizontal" align="center" justify="space-between">
        <Stack space={3} orientation="horizontal" align="center">
          <Badge
            variant={getStatusCodeBadgeVariant(response.statusCode)}
            text={response.statusCode ?? t('noStatus')}
          />
          <Text font="regular14" text={requestTypeLabel} />
        </Stack>
        <Text
          font="regular14"
          color="textHelper"
          text={abbreviateDate(response.dateCreated, 'en', 'datetime')}
        />
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    emptyHeader: 'No requests yet',
    emptyContent: "This action hasn't made any requests yet.",
    noStatus: 'No status',
    unknownType: 'Unknown',
  },
};
